import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories, kaspreadsheetUrl } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  }, [location.pathname]);

  const isTransparent = !scrolled && isHome;

  const linkClass = `px-4 py-2 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap ${
    isTransparent
      ? "text-white/90 hover:bg-white/15 hover:text-white"
      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-900"
  }`;

  const brandTextClass = `font-bold text-lg whitespace-nowrap font-heading ${
    isTransparent ? "text-white" : "text-foreground-900"
  }`;

  const brandSubClass = `font-normal ${
    isTransparent ? "text-white/70" : "text-foreground-500"
  }`;

  const mobileToggleClass = `md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
    isTransparent
      ? "text-white hover:bg-white/15"
      : "text-foreground-700 hover:bg-background-100"
  }`;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "navbar-scrolled border-b border-background-200/70"
          : isHome
          ? "bg-transparent"
          : "bg-white border-b border-background-200/70"
      }`}
    >
      <div className="mx-auto flex items-center justify-between h-[72px] px-4 md:px-8">
        <div className="flex items-center gap-6 md:gap-8">
          <Link to="/" className="flex items-center gap-2.5 no-underline group">
            <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
              <i className="ri-store-2-line text-background-50 text-lg"></i>
            </div>
            <span className={brandTextClass}>
              hoobuy<span className={brandSubClass}>.biz</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link to="/" className={linkClass}>
              Início
            </Link>

            <div className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                className={`flex items-center gap-1.5 ${linkClass} cursor-pointer`}
              >
                Categorias
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl border border-background-200/70 py-2 max-h-[420px] overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-secondary-50 transition-colors whitespace-nowrap"
                    >
                      <span className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className={`ri-${getCatIcon(cat.slug)} text-foreground-500 text-xs`}></i>
                      </span>
                      <span>{cat.name}</span>
                      <i className="ri-arrow-right-up-line text-foreground-400 text-xs ml-auto"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/artigo/guia-compras-hoobuy-iniciantes"
              className={linkClass}
            >
              Guia de Compras
            </Link>

            <Link
              to="/artigo/guia-completo-hoobuyspreadsheet"
              className={linkClass}
            >
              Artigos
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={kaspreadsheetUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer"
          >
            hoobuy sheet
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={mobileToggleClass}
          >
            <i
              className={`text-xl ${
                mobileMenuOpen ? "ri-close-line" : "ri-menu-line"
              }`}
            ></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200/70 max-h-[calc(100vh-72px)] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-foreground-700 text-sm font-semibold rounded-lg hover:bg-background-100 transition-colors"
            >
              <i className="ri-home-4-line text-lg text-foreground-500"></i>
              Início
            </Link>

            <button
              type="button"
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-foreground-700 text-sm font-semibold rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-lg text-foreground-500"></i>
                Categorias
              </span>
              <i
                className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${
                  mobileCatOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200/70 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-between px-4 py-3 text-sm text-foreground-600 hover:bg-secondary-50 rounded-lg transition-colors"
                  >
                    <span className="flex items-center gap-2.5">
                      <span className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center">
                        <i className={`ri-${getCatIcon(cat.slug)} text-foreground-500 text-xs`}></i>
                      </span>
                      {cat.name}
                    </span>
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/artigo/guia-compras-hoobuy-iniciantes"
              className="flex items-center gap-3 px-4 py-3 text-foreground-700 text-sm font-semibold rounded-lg hover:bg-background-100 transition-colors"
            >
              <i className="ri-book-open-line text-lg text-foreground-500"></i>
              Guia de Compras
            </Link>

            <Link
              to="/artigo/guia-completo-hoobuyspreadsheet"
              className="flex items-center gap-3 px-4 py-3 text-foreground-700 text-sm font-semibold rounded-lg hover:bg-background-100 transition-colors"
            >
              <i className="ri-article-line text-lg text-foreground-500"></i>
              Artigos SEO
            </Link>
          </div>

          <div className="px-4 pb-6 pt-2">
            <a
              href={kaspreadsheetUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all"
            >
              hoobuy sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

function getCatIcon(slug: string): string {
  const iconMap: Record<string, string> = {
    shoes: "footprint-line",
    "hoodies-sweaters": "t-shirt-line",
    "t-shirts": "shirt-line",
    jackets: "rainbow-line",
    "pants-shorts": "trousers-line",
    headwear: "capsule-line",
    sets: "stack-line",
    underwear: "checkbox-blank-circle-line",
    jersey: "team-line",
    accessories: "glasses-line",
    bags: "shopping-bag-line",
    belts: "link-line",
    perfumes: "drop-line",
    "other-stuff": "more-line",
  };
  return iconMap[slug] || "arrow-right-line";
}